/*
  Free gift guard (Yarn retail, 9 Oct 2026)
  Trims $0 Rebuy free gifts (products tagged freegift) to what the cart has earned.
  Eligibility comes from the Rebuy gift widgets' own rules, so changing a tier in Rebuy needs no theme change.

  Failsafes:
  - Only lines that are $0 AND tagged freegift are ever touched (decided server-side in templates/cart.gift-guard.liquid).
    Paid lines are never changed. Gift lines are only ever reduced, never added.
  - Fails open: any error, timeout, unexpected response, unknown Rebuy rule, other currency, or Rebuy saying
    "not eligible" when its own rule says the cart qualifies => change nothing.
  - Never acts on a stale cart: re-reads the cart right before writing and drops the change if anything moved.
  - Never writes while Rebuy is changing the cart (19 Oct 2026). Rebuy's gift widgets remove an unearned gift
    themselves; a guard write landing on top of theirs made Rebuy repeat the removal on its stale copy of the cart
    (422 "updates parameter is invalid.", and its cart kept showing the removed gift). The guard now waits until
    Rebuy's cart is quiet and Rebuy has had time to act, re-reads, and leaves it alone if Rebuy already did the job.
  - At most 3 corrections per minute per browser tab (kept across reloads), and the /cart page only reloads after
    a verified change, so it can never reload in a loop.
  - Multi-buy deals (20 Oct 2026): Ultimate Special Offers takes its deals ("2 for $80") off at checkout, so the
    customer pays less than the cart's own total. Gifts are judged on the total after the deal, the figure the cart
    redesign also shows, using USO's own answer for this cart. No answer, no USO on the page, or an answer that makes
    no sense => the cart's own total, exactly as before.
  - Mode from Theme settings > Free gift guard: on | watch | off. Watch writes the note but changes nothing.
*/
(function () {
  'use strict';

  var SUBTOTAL_OPERATORS = { greater_than: function (s, t) { return s > t; }, greater_than_or_equal: function (s, t) { return s >= t; }, greater_than_or_equal_to: function (s, t) { return s >= t; } };

  function has(obj, key) { return Object.prototype.hasOwnProperty.call(obj, key); }

  // Reads a Rebuy data-source response for one gift widget, asked with cart_subtotal = spend (cents).
  // Returns { status: 'matched' | 'unmatched' | 'unknown', threshold: cents or null }.
  // If Rebuy says unmatched but its own subtotal rule says the cart qualifies, the answer is 'unknown' (do nothing).
  function evaluateRebuyResponse(json, spend) {
    var unknown = { status: 'unknown', threshold: null };
    var meta = json && json.metadata;
    if (!meta || !Array.isArray(meta.matched_rules) || !Array.isArray(meta.unmatched_rules)) return unknown;
    if ((meta.unevaluated_rules || []).length || (meta.errors || []).length) return unknown;
    var groups = meta.matched_rules.concat(meta.unmatched_rules);
    if (!groups.length) return unknown;
    var subtotalRules = [];
    for (var g = 0; g < groups.length; g++) {
      var logic = groups[g].logic || [];
      if (!logic.length) return unknown;
      for (var l = 0; l < logic.length; l++) {
        var rules = logic[l].rules || [];
        for (var r = 0; r < rules.length; r++) {
          var rule = rules[r];
          if (rule.type === 'product' && rule.operator === 'anything') continue;
          if (rule.type === 'cart_subtotal' && has(SUBTOTAL_OPERATORS, rule.operator) && !isNaN(parseFloat(rule.value))) {
            subtotalRules.push({ op: rule.operator, cents: Math.round(parseFloat(rule.value) * 100) });
            continue;
          }
          return unknown;
        }
      }
    }
    var status = meta.matched_rules.length > 0 ? 'matched' : 'unmatched';
    var threshold = null;
    subtotalRules.forEach(function (s) { if (threshold === null || s.cents < threshold) threshold = s.cents; });
    if (status === 'unmatched') {
      // Cross-check: only trust "not eligible" when there is exactly one subtotal rule and the cart really is under it.
      if (groups.length !== 1 || subtotalRules.length !== 1 || typeof spend !== 'number') return unknown;
      if (SUBTOTAL_OPERATORS[subtotalRules[0].op](spend, subtotalRules[0].cents)) return unknown;
    }
    return { status: status, threshold: threshold };
  }

  // Pure decision. lines: gift lines in cart order ({ key, quantity, widget_id, sku }).
  // verdicts: { widgetId: { status, threshold } } for every gift widget.
  // Returns null when anything is not a clear matched/unmatched (fail open), else { updates: { key: qty }, actions, allowed, kept }.
  function decide(lines, verdicts) {
    var ids = Object.keys(verdicts);
    if (!ids.length) return null;
    for (var i = 0; i < ids.length; i++) {
      var s = verdicts[ids[i]] && verdicts[ids[i]].status;
      if (s !== 'matched' && s !== 'unmatched') return null;
    }

    var allowed = ids.filter(function (id) { return verdicts[id].status === 'matched'; }).length;
    var updates = {}, actions = [], keptByWidget = {}, kept = 0, pool = [];

    lines.forEach(function (line) {
      var w = line.widget_id && has(verdicts, line.widget_id) ? line.widget_id : null;
      if (!w) { pool.push(line); return; }
      if (verdicts[w].status !== 'matched') {
        updates[line.key] = 0; actions.push({ line: line, to: 0, why: 'below' });
      } else if (has(keptByWidget, w)) {
        pool.push(line); // a second gift from the same widget can still use a free slot from another earned tier
      } else {
        keptByWidget[w] = true; kept++;
        if (line.quantity > 1) { updates[line.key] = 1; actions.push({ line: line, to: 1, why: 'qty' }); }
      }
    });

    pool.forEach(function (line) {
      if (kept < allowed) {
        kept++;
        if (line.quantity > 1) { updates[line.key] = 1; actions.push({ line: line, to: 1, why: 'qty' }); }
      } else {
        updates[line.key] = 0; actions.push({ line: line, to: 0, why: allowed === 0 ? 'below' : 'extra' });
      }
    });

    return { updates: updates, actions: actions, allowed: allowed, kept: kept };
  }

  // May the guard write to the cart now? rebuyCart is window.Rebuy.Cart (undefined before Rebuy loads).
  // Rebuy's gift widgets act about half a second after each Rebuy cart change (later if Rebuy is slow), so the guard
  // also waits `grace` ms after the last one. No Rebuy means nothing to wait for; a Rebuy that cannot say whether it
  // is busy gets the grace only.
  function rebuySettled(rebuyCart, msSinceCartEvent, grace) {
    if (!rebuyCart) return true;
    if (typeof rebuyCart.isCartQuiescent === 'function') {
      try { if (rebuyCart.isCartQuiescent() !== true) return false; } catch (e) { return false; }
    }
    return msSinceCartEvent >= grace;
  }

  // The saving in USO's reply for the cart (its discounts.total_discount_amount, in dollars), in cents; 0 if none or
  // unclear, including a reply that does not add up (total before, less the saving, must be the total after).
  function dealFromReply(json) {
    var d = json && json.discounts;
    var amount = d ? parseFloat(d.total_discount_amount) : NaN;
    if (!isFinite(amount) || amount <= 0) return 0;
    var before = d.cart ? parseFloat(d.cart.original_total_price) : NaN;
    var after = parseFloat(d.discounted_price);
    if (isFinite(before) && isFinite(after) && Math.abs(before - after - amount) > 0.05) return 0;
    return Math.round(amount * 100);
  }

  // The total gifts are judged on: the cart's total less the deal. A deal that is not less than the total makes no
  // sense, so it is ignored.
  function spendAfterDeal(spend, deal) {
    return deal > 0 && deal < spend ? spend - deal : spend;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { decide: decide, evaluateRebuyResponse: evaluateRebuyResponse, rebuySettled: rebuySettled, dealFromReply: dealFromReply, spendAfterDeal: spendAfterDeal };
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var cfg = window.__giftGuard || {};
  var MODE = cfg.mode === 'watch' ? 'watch' : (cfg.mode === 'off' ? 'off' : 'on');
  if (MODE === 'off') return;
  if (window.Shopify && window.Shopify.designMode) return;

  var LOG = '[gift-guard]';
  var API = 'https://rebuyengine.com/api/v1';
  var CAP = 3, CAP_WINDOW = 60000, CAP_KEY = 'gift-guard-corrections', MSG_KEY = 'gift-guard-msg';
  var REBUY_GRACE = 3000, REBUY_POLL = 100, REBUY_CEILING = 10000, REBUY_RETRIES = 3;
  var DEFAULT_WIDGETS = String(cfg.widgets || '').split(',').map(function (s) { return s.trim(); }).filter(function (s) { return /^\d+$/.test(s); });
  var USO_ENDPOINT = cfg.usoEndpoint || 'https://specialoffers.suppleapps.com/api/v2/page-actions?data-product-handles';
  var USO_SKIP = ['page_actions_data', 'discounts', 'action_crosssell_el', 'action_crosssell_popup_action', 'action_crosssell_popup_html'];
  var endpointCache = {};
  var verdictCache = {};
  var running = false, rerun = false, timer = null, generation = 0, memoryCorrections = [];
  var lastCartEvent = 0, rebuyWaits = 0;

  function log() { try { console.info.apply(console, [LOG].concat([].slice.call(arguments))); } catch (e) {} }

  function fetchJSON(url, options) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var t = ctrl ? setTimeout(function () { ctrl.abort(); }, 6000) : null;
    var opts = Object.assign({ credentials: 'same-origin', cache: 'no-store' }, options || {});
    if (ctrl) opts.signal = ctrl.signal;
    return fetch(url, opts)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status + ' ' + url);
        return res.text();
      })
      .then(function (text) { if (t) clearTimeout(t); return JSON.parse(text); }, function (err) { if (t) clearTimeout(t); throw err; });
  }

  function rebuyKey() {
    return (window.Rebuy && window.Rebuy.shop && window.Rebuy.shop.api_key) || cfg.rebuyKey || '';
  }

  function widgetEndpoint(id) {
    if (has(endpointCache, id)) return Promise.resolve(endpointCache[id]);
    return fetchJSON(API + '/widgets/settings?id=' + encodeURIComponent(id) + '&key=' + encodeURIComponent(rebuyKey()), { credentials: 'omit' })
      .then(function (json) {
        var ep = json && json.data && json.data.endpoint;
        endpointCache[id] = (typeof ep === 'string' && /^\/custom\/id\/\d+$/.test(ep)) ? ep : null;
        return endpointCache[id];
      });
  }

  function widgetVerdict(id, spend) {
    return widgetEndpoint(id).then(function (ep) {
      if (!ep) return { status: 'unknown', threshold: null };
      var cacheKey = ep + '|' + spend;
      if (has(verdictCache, cacheKey)) return verdictCache[cacheKey];
      return fetchJSON(API + ep + '?key=' + encodeURIComponent(rebuyKey()) + '&cart_subtotal=' + spend + '&limit=1', { credentials: 'omit' })
        .then(function (json) { verdictCache[cacheKey] = evaluateRebuyResponse(json, spend); return verdictCache[cacheKey]; });
    });
  }

  // The multi-buy deal on this cart, in cents, from USO's server (the request USO's own script makes: window.saso
  // less its page-only keys, with the cart). 0 when USO is not on the page or does not answer.
  function usoDeal() {
    // USO's checkout must be on the page too: without it checkout is Shopify's own, with no deal.
    if (!window.saso || typeof window.saso !== 'object' || typeof window.sasoCheckout !== 'function') return Promise.resolve(0);
    return fetchJSON('/cart.js')
      .then(function (cart) {
        var body = {};
        Object.keys(window.saso).forEach(function (k) { if (USO_SKIP.indexOf(k) < 0) body[k] = window.saso[k]; });
        body.cart = cart;
        body.page_type = 'cart';
        return fetchJSON(USO_ENDPOINT, { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json; charset=utf-8' }, body: JSON.stringify(body) });
      })
      .then(dealFromReply, function (err) { log('no deal answer from USO, using the cart total:', err && err.message ? err.message : err); return 0; });
  }

  function money(cents) { return '$' + (cents / 100).toFixed(2); }

  // Note core has no timestamp, so the same situation is never written twice.
  function noteCore(result, spend, mode, deal) {
    var parts = result.actions.map(function (a) { return (a.line.sku || a.line.variant_id) + ' ' + a.line.quantity + '>' + a.to + ' (' + a.why + ')'; });
    return ((mode === 'watch' ? 'WOULD ' : '') + 'trim: ' + parts.join(', ') + ' | spend ' + money(spend) + (deal ? ' (deal -' + money(deal) + ')' : '') + ' | earned ' + result.allowed).slice(0, 220);
  }

  function customerMessage(result, verdicts, spend, deal) {
    var whys = result.actions.map(function (a) { return a.why; });
    if (whys.indexOf('below') > -1) {
      // The deal wording only when the deal is what took the cart under a tier it otherwise reaches.
      var byDeal = deal > 0 && Object.keys(verdicts).some(function (id) {
        var v = verdicts[id];
        return v.status === 'unmatched' && v.threshold !== null && spend + deal > v.threshold;
      });
      var next = null;
      Object.keys(verdicts).forEach(function (id) {
        var v = verdicts[id];
        if (v.status === 'unmatched' && v.threshold !== null && v.threshold >= spend && (next === null || v.threshold < next)) next = v.threshold;
      });
      // Same figure as Rebuy's "You are $X away" bar (threshold minus cart).
      var more = next !== null ? Math.max(next - spend, 1) : null;
      var removed = result.actions.filter(function (a) { return a.to === 0; }).length;
      var why = byDeal ? ' because, with your deal applied, your cart no longer qualifies' : ' because your cart no longer qualifies';
      var lead = result.kept > 0
        ? 'One of your free gifts has been removed' + why + ' for it.'
        : (removed > 1 ? 'Your free gifts have been removed' : 'Your free gift has been removed') + why + '.';
      return lead + (more !== null ? ' You are ' + money(more) + ' away from unlocking it again.' : '');
    }
    if (whys.indexOf('extra') > -1) return 'Your cart qualifies for ' + result.allowed + ' free gift' + (result.allowed === 1 ? '' : 's') + ', so we have removed the extra one.';
    return 'Free gifts are limited to one of each, so we have updated the quantity.';
  }

  // Top of the screen, so it never covers the cart's checkout button.
  function toast(message) {
    try {
      var old = document.getElementById('gift-guard-toast');
      if (old) old.remove();
      var el = document.createElement('div');
      el.id = 'gift-guard-toast';
      el.setAttribute('role', 'status');
      el.style.cssText = 'position:fixed;left:16px;right:16px;top:16px;margin:0 auto;max-width:420px;box-sizing:border-box;background:#003b49;color:#fff;padding:14px 40px 14px 16px;border-radius:8px;font-size:14px;line-height:1.4;box-shadow:0 6px 24px rgba(0,0,0,.25);z-index:2147483647;';
      el.textContent = message;
      var close = document.createElement('button');
      close.type = 'button';
      close.setAttribute('aria-label', 'Close');
      close.textContent = '×';
      close.style.cssText = 'position:absolute;top:6px;right:8px;background:none;border:0;color:#fff;font-size:20px;line-height:1;cursor:pointer;';
      close.onclick = function () { el.remove(); };
      el.appendChild(close);
      document.body.appendChild(el);
      setTimeout(function () { if (el.parentNode) el.remove(); }, 9000);
    } catch (e) {}
  }

  // Correction cap, kept in sessionStorage so it survives reloads. Returns { ok, persistent }.
  function takeCorrectionSlot() {
    var now = Date.now();
    try {
      var list = JSON.parse(sessionStorage.getItem(CAP_KEY) || '[]').filter(function (t) { return typeof t === 'number' && now - t < CAP_WINDOW; });
      if (list.length >= CAP) return { ok: false, persistent: true };
      list.push(now);
      sessionStorage.setItem(CAP_KEY, JSON.stringify(list));
      return { ok: true, persistent: true };
    } catch (e) {
      memoryCorrections = memoryCorrections.filter(function (t) { return now - t < CAP_WINDOW; });
      if (memoryCorrections.length >= CAP) return { ok: false, persistent: false };
      memoryCorrections.push(now);
      return { ok: true, persistent: false };
    }
  }

  function signature(cart) {
    return String(cart.items_subtotal_price) + '|' + cart.items.map(function (i) { return i.key + ':' + i.quantity; }).join(',');
  }

  // True only if every requested change is reflected in Shopify's response.
  function updateApplied(updates, cartAfter) {
    if (!cartAfter || !Array.isArray(cartAfter.items)) return false;
    return Object.keys(updates).every(function (key) {
      var line = cartAfter.items.filter(function (i) { return i.key === key; })[0];
      return updates[key] === 0 ? !line : (line && line.quantity === updates[key]);
    });
  }

  function rebuyQuiet(grace) {
    return rebuySettled(window.Rebuy && window.Rebuy.Cart, Date.now() - lastCartEvent, grace === undefined ? REBUY_GRACE : grace);
  }

  // Resolves true once Rebuy's cart is quiet (nothing queued or in flight, its last write read back) and its gift
  // widgets have had their turn; false if that has not happened within REBUY_CEILING.
  function waitForRebuy() {
    var started = Date.now();
    return new Promise(function (resolve) {
      (function poll() {
        if (rebuyQuiet()) return resolve(true);
        if (Date.now() - started >= REBUY_CEILING) return resolve(false);
        setTimeout(poll, REBUY_POLL);
      })();
    });
  }

  // Rebuy stayed busy: do nothing now and look again shortly, a few times. After that the guard only acts again
  // on a cart change that finds Rebuy quiet (see check), so it can never keep waking itself up.
  // An idle Rebuy that never read back its last write stays "busy" until it re-reads, so ask it to.
  function retryAfterRebuy() {
    rebuyWaits++;
    if (rebuyWaits >= REBUY_RETRIES) { log('Rebuy cart still busy; leaving it until Rebuy is quiet'); return; }
    log('Rebuy cart busy; checking again shortly');
    try { if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchCart === 'function') window.Rebuy.Cart.fetchCart(); } catch (e) {}
    rerun = true;
  }

  function check() {
    if (running) { rerun = true; return; }
    running = true;
    var startGeneration = generation;
    var snapshot = null;
    fetchJSON('/cart?view=gift-guard')
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        if (cfg.shopCurrency && cart.currency !== cfg.shopCurrency) return;
        var gifts = cart.items.filter(function (i) { return i.gift === true && i.original_price === 0; });
        if (!gifts.length) return;
        if (!rebuyKey()) return;
        snapshot = signature(cart);
        var cartSpend = Math.max(0, parseInt(cart.items_subtotal_price, 10) || 0);
        var spend = cartSpend, deal = 0;
        var ids = DEFAULT_WIDGETS.slice();
        gifts.forEach(function (g) { if (g.widget_id && /^\d+$/.test(g.widget_id) && ids.indexOf(g.widget_id) < 0) ids.push(g.widget_id); });
        if (!ids.length) return;
        // Judged on the total after any multi-buy deal (see the header).
        return usoDeal().then(function (off) {
          spend = spendAfterDeal(cartSpend, off);
          deal = cartSpend - spend;
          return Promise.all(ids.map(function (id) { return widgetVerdict(id, spend); }));
        }).then(function (results) {
          var verdicts = {};
          ids.forEach(function (id, n) { verdicts[id] = results[n]; });
          var result = decide(gifts, verdicts);
          if (!result) { log('Rebuy answer unclear; doing nothing', verdicts); return; }
          if (!result.actions.length) return;
          if (generation !== startGeneration) { rerun = true; return; }

          // Already gave up on a Rebuy that stays busy: no more waiting or nudging until it is quiet.
          if (rebuyWaits >= REBUY_RETRIES && !rebuyQuiet(0)) return;

          // Let Rebuy finish first: its own gift widgets usually remove an unearned gift themselves.
          return waitForRebuy().then(function (quiet) {
            if (!quiet) { retryAfterRebuy(); return; }
            rebuyWaits = 0;
            if (generation !== startGeneration) { rerun = true; return; }
            // Re-read the cart right before writing; drop the change if anything moved meanwhile
            // (including Rebuy having removed the gift already).
            return fetchJSON('/cart?view=gift-guard');
          }).then(function (fresh) {
            if (fresh === undefined) return;
            if (!fresh || !Array.isArray(fresh.items) || signature(fresh) !== snapshot || generation !== startGeneration) { rerun = true; return; }
            if (!rebuyQuiet()) { rerun = true; return; } // Rebuy started changing the cart during the re-read
            var core = noteCore(result, spend, MODE, deal);
            var note = core + ' | ' + new Date().toISOString();

            if (MODE === 'watch') {
              log('watch only, would change', result.updates);
              if (fresh.note && fresh.note.indexOf(core) === 0) return;
              return fetchJSON('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { _gift_guard: note } }) });
            }

            var slot = takeCorrectionSlot();
            if (!slot.ok) { log('correction cap reached; doing nothing'); return; }
            var message = customerMessage(result, verdicts, spend, deal);
            log('trimming gifts', result.updates);
            return fetchJSON('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates: result.updates, attributes: { _gift_guard: note } })
            }).then(function (after) {
              if (!updateApplied(result.updates, after)) { log('Shopify did not apply the change; not repeating', result.updates); return; }
              toast(message);
              try { if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchCart === 'function') window.Rebuy.Cart.fetchCart(); } catch (e) {}
              // The /cart page is server-rendered: reload once so it shows the change. Only with a persistent cap.
              if (cfg.isCartPage && slot.persistent) {
                try { sessionStorage.setItem(MSG_KEY, message); } catch (e) {}
                location.reload();
              }
            });
          });
        });
      })
      .catch(function (err) { log('error, doing nothing:', err && err.message ? err.message : err); })
      .then(function () {
        running = false;
        if (rerun) { rerun = false; schedule(); }
      });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(check, 700);
  }

  function onCartEvent() { generation++; lastCartEvent = Date.now(); schedule(); }
  ['rebuy:cart.change', 'cart:updated', 'cart:refresh'].forEach(function (name) { document.addEventListener(name, onCartEvent); });

  function start() {
    if (cfg.isCartPage) {
      try {
        var msg = sessionStorage.getItem(MSG_KEY);
        if (msg) { sessionStorage.removeItem(MSG_KEY); toast(msg); }
      } catch (e) {}
    }
    // On page load only check when the server says the cart holds a gift; cart events always check.
    if (cfg.hasGift) setTimeout(check, 1500);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
